import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api';
import { jobEndpoints, appointmentAuthEndpoints } from '@/lib/api/endpoints';
import { jobKeys, isJobAppointmentsQuery } from '@/lib/queryKeys';
import type { AuthorizationLevel, AuthorizationMethod, Job, JobAuthorization } from '@/types/job';

export interface AuthorizationConfigDto {
  required: boolean;
  level: AuthorizationLevel;
  method: AuthorizationMethod;
  scopeSummary?: string;
  maxAmount?: number;
  notes?: string;
}

export function useUpdateAuthorizationConfig(entity: 'job' | 'appointment' = 'job') {
  const qc = useQueryClient();
  return useApiMutation<Job | JobAuthorization, AuthorizationConfigDto & { id: string }>({
    mutationFn: ({ id, ...dto }) => {
      const url =
        entity === 'job'
          ? jobEndpoints.authorization(id)
          : appointmentAuthEndpoints.authorization(id);
      return apiPatch(operationsClient, url, dto);
    },
    onSuccess: (_data, vars) => {
      if (entity === 'job') {
        void qc.invalidateQueries({ queryKey: jobKeys.detail(vars.id) });
      } else {
        // The mutation only carries the appointment id, not its jobId, so match
        // any cached job-appointments list/detail query and refetch it.
        void qc.invalidateQueries({ predicate: isJobAppointmentsQuery });
      }
      toast.success('Authorization settings updated');
    },
  });
}
